import { useCallback, useRef } from 'react';

/**
 * useMilestones — watch-through milestones (25/50/75/100), fired once per
 * page view. A seek that reaches a milestone counts: this measures engagement
 * DEPTH, not strict linear viewing (the established hero semantics). 99% is
 * reported as 100 so loops/rounding can't starve the terminal milestone.
 *
 * Callback-based (no element ref) so it composes with useVideoEngine's
 * onTimeUpdate regardless of when the <video> mounts:
 *   const { checkMilestones } = useMilestones((m) =>
 *     trackEvent(`video_progress_${m}`, analyticsContext));
 *   ...onTimeUpdate={(t, d) => checkMilestones(t, d)}
 *
 * Event NAMES encode the percent (video_progress_25 ...) — queryable in GA4
 * without custom-dimension registration (house canon).
 */
const useMilestones = (onMilestone) => {
  const sentRef = useRef(new Set());
  const onMilestoneRef = useRef(onMilestone);
  onMilestoneRef.current = onMilestone;

  const checkMilestones = useCallback((currentTime, duration) => {
    if (!duration) return;
    const pct = (currentTime / duration) * 100;
    [25, 50, 75, 99].forEach((m) => {
      if (pct >= m && !sentRef.current.has(m)) {
        sentRef.current.add(m);
        if (onMilestoneRef.current) onMilestoneRef.current(m === 99 ? 100 : m);
      }
    });
  }, []);

  /** Reset for a new source (per-view semantics follow the SOURCE, not the mount). */
  const resetMilestones = useCallback(() => { sentRef.current = new Set(); }, []);

  return { checkMilestones, resetMilestones };
};

export default useMilestones;
